/**
 * LivePortTable — what is on each physical port RIGHT NOW, for an AP or a switch.
 *
 * WHY (owner, 2026-10-12, JH-BasementTV EAP104): the Ports section was static tiles — "WAN 1
 * Trunk uplink / LAN 1–4 Untagged device config" — "I cannot see the ports properly like the
 * clients or the VLANs configured". This table reads GET /devices/{serial}/ports/live
 * (services/live_ports): one row per physical port —
 *
 *     Port | Link (speed) | Mode & VLANs (untagged/tagged chips) | Connected | Traffic
 *
 * and a row opens a detail card underneath (the switch faceplate's pattern) listing the clients
 * / learned MACs, the LLDP neighbour and the VLANs it advertises, and where each fact came from.
 *
 * HONESTY: the header says how old the state report is; a firmware that does not attribute
 * clients to a port says so ("3 on LAN 1–4 (port not reported)") instead of showing an empty
 * cell; a rate needs two samples. All wording lives in lib/livePorts (unit-tested).
 *
 * The same component serves switches (kind="switch"): the response shape is identical, with the
 * VLANs taken from the current-VLANs merge (OP#1639) and MACs per VLAN from the MAC table.
 *
 * Editing is NOT here: callers pass `renderPortActions` (the AP override editor / the switch
 * faceplate keep their own propose-then-confirm flows).
 */
import { useMemo, useState, type ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataGrid } from "@/components/data/DataGrid";
import { StatusPill } from "@/components/ui/StatusPill";
import { useLivePorts } from "@/lib/api/hooks";
import { cn } from "@/lib/cn";
import {
  clientName,
  clientsEmptyText,
  connectedSummary,
  expandedClients,
  groupLabel,
  linkLabel,
  linkStatus,
  LIVE_PORTS_UNAVAILABLE,
  modeLabel,
  normalizeLivePorts,
  neighbourName,
  provenanceLine,
  trafficLabel,
  fmtBytesShort,
  vlanChips,
  type LiveClient,
  type LiveClientGroup,
  type LivePort,
  type LivePortsResponse,
  type VlanChip,
} from "@/lib/livePorts";

/** One VLAN chip. Untagged = solid hairline; tagged = dashed; routed/notes = muted ground. */
function Chip({ chip }: { chip: VlanChip }) {
  return (
    <span
      title={chip.title}
      className={cn(
        "inline-flex max-w-full items-center rounded border px-1.5 py-0.5 font-mono text-[11px] tabular-nums",
        chip.kind === "untagged" && "border-ink-subtle bg-surface text-ink",
        chip.kind === "tagged" && "border-dashed border-line bg-surface text-ink",
        chip.kind === "routed" && "border-line bg-surface-2 text-ink",
        chip.kind === "note" && "border-transparent bg-surface-2 font-sans text-ink-muted",
      )}
    >
      {chip.text}
    </span>
  );
}

function ModeCell({ port }: { port: LivePort }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs text-ink-muted">{modeLabel(port)}</span>
      <div className="flex flex-wrap gap-1">
        {vlanChips(port.config).map((c) => (
          <Chip key={`${c.kind}:${c.text}`} chip={c} />
        ))}
      </div>
    </div>
  );
}

function LinkCell({ port }: { port: LivePort }) {
  return (
    <div className="flex flex-col gap-0.5">
      <StatusPill status={linkStatus(port.link)} hollow={!port.link.up}>
        {linkLabel(port.link)}
      </StatusPill>
      {port.poe && port.poe.power_mw != null && port.poe.power_mw > 0 && (
        <span className="text-[11px] text-ink-muted">PoE {(port.poe.power_mw / 1000).toFixed(1)} W</span>
      )}
      {port.stp?.state && port.stp.state !== "Forwarding" && (
        <span className="text-[11px] text-warn">STP {port.stp.state}</span>
      )}
    </div>
  );
}

export function LivePortTable({
  serial,
  renderPortActions,
  enabled = true,
  fallback,
}: {
  serial: string;
  /** Per-port actions shown in the expanded card (e.g. the AP override "Edit"). */
  renderPortActions?: (port: LivePort) => ReactNode;
  enabled?: boolean;
  /** Rendered under the error line when the live view cannot be read (e.g. read_only: 403). */
  fallback?: ReactNode;
}) {
  const { data: raw, isLoading, error } = useLivePorts(serial, enabled);
  // Guard the body BEFORE anything reads it: a response without `ports` (older controller,
  // empty fallback body) used to crash the whole device sheet on `data.ports.find`.
  const data = useMemo(() => (raw === undefined ? undefined : normalizeLivePorts(raw)), [raw]);
  const [open, setOpen] = useState<string | null>(null);
  const groups = data?.client_groups ?? [];
  const selected = useMemo(() => data?.ports.find((p) => p.name === open) ?? null, [data, open]);

  const columns = useMemo<ColumnDef<LivePort, unknown>[]>(
    () => [
      {
        id: "port",
        header: "Port",
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-mono text-sm font-medium text-ink">{row.original.label}</span>
            <span className="text-[11px] text-ink-subtle">{row.original.role_label}</span>
          </div>
        ),
      },
      { id: "link", header: "Link", enableSorting: false, cell: ({ row }) => <LinkCell port={row.original} /> },
      { id: "mode", header: "Mode & VLANs", enableSorting: false, cell: ({ row }) => <ModeCell port={row.original} /> },
      {
        id: "connected",
        header: "Connected",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-2 text-xs text-ink">{connectedSummary(row.original, groups)}</span>
        ),
      },
      {
        id: "traffic",
        header: "Traffic",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-[11px] tabular-nums text-ink-muted">
            {trafficLabel(row.original.traffic)}
          </span>
        ),
      },
    ],
    [groups],
  );

  if (error) {
    const status = (error as { status?: number }).status;
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-ink-muted">
          {status === 403
            ? "Live port details (clients, traffic) need the site operator role."
            : `Live port data is unavailable: ${(error as Error).message}`}
        </p>
        {fallback}
      </div>
    );
  }
  if (isLoading || data === undefined) return <p className="text-xs text-ink-muted">Reading this device's ports…</p>;
  if (data === null) {
    return (
      <div className="flex flex-col gap-2" data-testid="live-ports-unavailable">
        <p className="text-xs text-ink-muted">{LIVE_PORTS_UNAVAILABLE}</p>
        {fallback}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Provenance first: how old these numbers are is part of what they mean. */}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-subtle">
        <span>{provenanceLine(data)}</span>
        {data.source.stale && <StatusPill status="warn">Stale report</StatusPill>}
      </div>

      <DataGrid<LivePort>
        data={data.ports}
        columns={columns}
        rowKey={(p) => p.name}
        dense
        onRowActivate={(p) => setOpen(open === p.name ? null : p.name)}
        rowClassName={(p) => p.name === open && "bg-brand-soft"}
        empty={data.error ?? "This device reported no physical ports."}
        renderCard={(p) => (
          <div className={cn("flex flex-col gap-1.5", p.name === open && "ring-1 ring-brand")}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-medium text-ink">
                {p.label} <span className="font-sans text-[11px] font-normal text-ink-subtle">{p.role_label}</span>
              </span>
              <LinkCell port={p} />
            </div>
            <ModeCell port={p} />
            <span className="text-xs text-ink">{connectedSummary(p, groups)}</span>
            <span className="font-mono text-[11px] text-ink-muted">{trafficLabel(p.traffic)}</span>
          </div>
        )}
      />

      {selected && (
        <PortDetail port={selected} view={data} groups={groups} actions={renderPortActions?.(selected)} />
      )}

      {data.unattributed_clients.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-muted">
            {data.unattributed_clients.length} wired client
            {data.unattributed_clients.length === 1 ? "" : "s"} not attributed to a port
          </summary>
          <ClientList clients={data.unattributed_clients} />
        </details>
      )}

      {data.notes.length > 0 && (
        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-[11px] text-ink-subtle">
          {data.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The expanded row: configuration in words, LLDP, the client list, actions. */
function PortDetail({
  port,
  view,
  groups,
  actions,
}: {
  port: LivePort;
  view: LivePortsResponse;
  groups: LiveClientGroup[];
  actions?: ReactNode;
}) {
  const { clients, shared } = expandedClients(port, groups);
  return (
    <div className="md-card md-enter flex flex-col gap-3 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-sm font-medium text-ink">
          {port.label}
          <span className="ml-2 font-sans text-xs font-normal text-ink-muted">{port.role_label}</span>
        </span>
        {actions}
      </div>

      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-xs">
        <dt className="text-ink-muted">Configured</dt>
        <dd className="min-w-0 text-ink">
          {port.config.summary}
          {port.config.interface && <span className="text-ink-subtle"> · interface {port.config.interface}</span>}
          <span className="text-ink-subtle">
            {" · "}
            {port.config.source === "switch-vlans" ? "switch VLAN view" : port.config.source === "config" ? "configuration of record" : "no source"}
          </span>
        </dd>
        {port.vlans && port.vlans.drift.length > 0 && (
          <>
            <dt className="text-ink-muted">Drift</dt>
            <dd className="min-w-0 text-warn">{port.vlans.drift.map((d) => d.message).join(" · ")}</dd>
          </>
        )}
        {port.traffic && (
          <>
            <dt className="text-ink-muted">Counters</dt>
            <dd className="min-w-0 font-mono tabular-nums text-ink">
              {fmtBytesShort(port.traffic.tx_bytes)} out · {fmtBytesShort(port.traffic.rx_bytes)} in
              {(port.traffic.rx_errors || port.traffic.tx_errors) ? (
                <span className="text-warn"> · {(port.traffic.rx_errors ?? 0) + (port.traffic.tx_errors ?? 0)} errors</span>
              ) : null}
            </dd>
          </>
        )}
        <dt className="text-ink-muted">LLDP</dt>
        <dd className="min-w-0 text-ink">
          {port.lldp.length === 0 ? (
            <span className="text-ink-subtle">{view.reports.lldp ? "No neighbour on this port" : "Not reported"}</span>
          ) : (
            port.lldp.map((n, i) => (
              <div key={i} className="flex flex-col">
                <span>
                  <span className="font-medium">{neighbourName(n)}</span>
                  {n.port && <span className="text-ink-muted"> · {n.port}</span>}
                  {n.mgmt_ip && <span className="font-mono text-ink-muted"> · {n.mgmt_ip}</span>}
                </span>
                {n.vlans.length > 0 && (
                  <span className="text-[11px] text-ink-subtle">
                    Neighbour advertises VLANs{" "}
                    {n.vlans.map((v) => `${v.vlan}${v.native ? " (native)" : ""}`).join(", ")}
                  </span>
                )}
              </div>
            ))
          )}
        </dd>
      </dl>

      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-ink">
          {port.role === "port" ? "Learned MACs" : "Clients"}
          {shared && (
            <span className="ml-1 font-normal text-ink-muted">
              on {groupLabel(shared.ports)} — {shared.reason}
            </span>
          )}
        </span>
        {clients.length === 0 ? (
          <p className="text-xs text-ink-subtle">{clientsEmptyText(port, view)}</p>
        ) : (
          <ClientList clients={clients} />
        )}
      </div>
    </div>
  );
}

function ClientList({ clients }: { clients: LiveClient[] }) {
  return (
    <ul className="flex flex-col divide-y divide-line rounded border border-line">
      {clients.map((c) => (
        <li key={c.mac} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-2 py-1.5 text-xs">
          <span className="min-w-[8rem] font-medium text-ink">{clientName(c)}</span>
          <span className="font-mono text-ink-muted">{c.mac}</span>
          {c.ip && c.ip !== clientName(c) && <span className="font-mono text-ink-muted">{c.ip}</span>}
          {c.vendor && <span className="text-ink-subtle">{c.vendor}</span>}
          {c.vlans && c.vlans.length > 0 && (
            <span className="text-ink-subtle">VLAN {[...c.vlans].sort((a, b) => a - b).join(", ")}</span>
          )}
          {c.device && <span className="text-ink-subtle">Meridian {c.device.kind ?? "device"}</span>}
          {c.reason && <span className="text-ink-subtle">({c.reason})</span>}
        </li>
      ))}
    </ul>
  );
}
